// components/UserUI/Navigation/bottom-nav-bar-skeleton.tsx
//
// Suspense fallback for <BottomNavBarServer>, which does a `profiles` +
// `getSellerPaymentStatus` round-trip before it can render.
//
// Why this is a client component and not a static box: the real bar hides
// itself entirely on some routes/overlay states, so a static placeholder would
// flash a bar on `/chat` and `/profile/edit` that then vanishes. It reuses the
// canonical `deriveHideNavBar` (lib/nav/derive-nav-visibility.ts) rather than
// re-deriving the rules — one declaration, both consumers.
//
// `isMobileViewport: true` is asserted rather than measured: the whole bar is
// `md:hidden`, so the desktop case is already handled by CSS, and asserting
// mobile makes the skeleton hide in exactly the cases the real bar hides.
// `isSelectionModeActive: false` likewise — selection mode is user-driven state
// that cannot be active while this layout is still streaming its first render.
//
// @see .tondocs/infra/SPEC_ROOT_LAYOUT_STREAMING.md §7.2

"use client";

import { usePathname } from "next/navigation";
import { useOverlay } from "@/components/context/overlay-context";
import { deriveHideNavBar } from "@/lib/nav/derive-nav-visibility";
import { Skeleton } from "@/components/ui/skeleton";

export default function BottomNavBarSkeleton() {
  const pathname = usePathname();
  const { overlay } = useOverlay();

  const hideNavBar = deriveHideNavBar({
    overlay,
    pathname,
    isMobileViewport: true,
    isSelectionModeActive: false,
  });

  if (hideNavBar) return null;

  return (
    <nav
      aria-hidden
      className="fixed bottom-0 inset-x-0 z-50 md:hidden pointer-events-none"
    >
      <div className="w-full h-[calc(3.5rem+var(--kraahl-safe-bottom))] pb-[var(--kraahl-safe-bottom)] bg-background/95 backdrop-blur-xl border-t border-border/50">
        <div className="w-full h-full flex items-center justify-around px-2">
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="w-6 h-6 rounded-full" />
        </div>
      </div>
    </nav>
  );
}
